import { useAppDispatch } from "@/app/hooks";
import { getIconData } from "@/shared/utils/map";
import Switch from "@components/Switch";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import { EmeraldSelect } from "@emerald-react/select";
import { EmeraldTooltip } from "@emerald-react/tooltip";
import { useSurvey } from "@hooks/useSurveySelectors";
import { useUpdateSurveyMutation } from "@services/survey-core";
import { useUpdatePropertyMutation } from "@services/survey-properties";
import { Property } from "@survey-types/survey.property.types";
import { updateProperty } from "@survey/surveySlice";
import { BaseSyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import Swal from "sweetalert2";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getTourPropertyDisplayNumbers,
  resolveTourNumberingMode,
  setPersistedTourNumberingMode,
  TourNumberingMode,
} from "./tourNumbering";
import {
  useGetTourStopsQuery,
  useCreateTourStopMutation,
  useUpdateTourStopMutation,
  useDeleteTourStopMutation,
  useReorderTourStopsMutation,
  useAttachTourStopToPropertyMutation,
  useDetachTourStopFromPropertyMutation,
  TourStop,
} from "@services/survey-tour-stops";

interface TourScheduleDrawerProps {
  onClose: () => void;
}

interface PropertyItemProps {
  property: Property;
  displayNumber?: number;
  isIncludedInTourSchedule: boolean;
  isHidden: boolean;
  isCurrentLocation: boolean;
  onToggleIncludeInTourSchedule: (checked: boolean) => void | Promise<void>;
  stopDetailsValue: string;
  onStopDetailsChange: (e: BaseSyntheticEvent) => void;
  onStopDetailsFocus: () => void;
  onStopDetailsBlur: () => void;
  isDragging?: boolean;
}

interface CustomStopItemProps {
  stop: TourStop;
  stopDetailsValue: string;
  onStopDetailsChange: (e: BaseSyntheticEvent) => void;
  onStopDetailsFocus: () => void;
  onStopDetailsBlur: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

interface SortableItem {
  id: string;
  type: 'property' | 'customStop';
  order: number;
  data: Property | TourStop;
}

const CustomStopItem = ({
  stop,
  stopDetailsValue,
  onStopDetailsChange,
  onStopDetailsFocus,
  onStopDetailsBlur,
  onEdit,
  onDelete,
}: CustomStopItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `stop-${stop.stopId}`,
    data: {
      type: 'customStop',
      stop: stop,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="rounded-md border border-divider-1 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          className="mt-2 cursor-grab active:cursor-grabbing text-data-light hover:text-data"
          {...attributes}
          {...listeners}
        >
          <EmeraldIcon icon="drag_indicator" type="outlined" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center justify-center w-6 h-6">
              <EmeraldIcon 
                icon={stop.stopType === "CURRENT_LOCATION" ? "star" : "location_on"} 
                type={stop.stopType === "CURRENT_LOCATION" ? "filled" : "outlined"}
                className={`text-xl ${stop.stopType === "CURRENT_LOCATION" ? "text-warning-icon" : "text-primary"}`}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-data">{stop.label}</p>
              <p className="text-xs text-data-light">{stop.address || (stop.stopType === "CURRENT_LOCATION" ? "Current location" : "Custom location")}</p>
            </div>
          </div>
          <EmeraldInput
            helperText="Enter up to 200 characters"
            label="Stop details"
            type="text"
            placeholder=""
            value={stopDetailsValue}
            onChange={onStopDetailsChange}
            onFocus={onStopDetailsFocus}
            onBlur={onStopDetailsBlur}
            maxLength={200}
          />
        </div>
        <div className="flex gap-1">
          <EmeraldIconButton
            icon="edit"
            type="Icon"
            className="mt-2 text-data-light hover:text-data"
            onClick={onEdit}
          />
          <EmeraldIconButton
            icon="delete"
            type="Icon"
            className="mt-2 text-data-light hover:text-data"
            onClick={onDelete}
          />
        </div>
      </div>
    </li>
  );
};

const PropertyItem = ({
  property,
  displayNumber,
  isIncludedInTourSchedule,
  isHidden,
  isCurrentLocation,
  onToggleIncludeInTourSchedule,
  stopDetailsValue,
  onStopDetailsChange,
  onStopDetailsFocus,
  onStopDetailsBlur,
  isDragging = false,
}: PropertyItemProps) => {
  const isDisabled = !isIncludedInTourSchedule || isHidden || isCurrentLocation;
  const { setNodeRef, transform, transition } = useSortable({
    id: `property-${property.propertyId}`,
    data: {
      type: 'property',
      property: property,
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rounded-md border p-3 transition-colors ${
        isDisabled
          ? "border-divider-2 bg-gray-100" 
          : "border-divider-1 bg-white"
      }`}
    >
      <div className="flex items-start gap-3 cursor-default">
        <div className="flex-1">
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-start gap-2">
              <div className="shrink-0">
                <div
                  className={`text-center rounded-full w-5 h-5 flex items-center justify-center text-white text-xs font-medium ${
                    isDisabled ? "opacity-50" : ""
                  }`}
                  style={{
                    backgroundColor: property?.isClientShortlist
                      ? "#003f2d"
                      : getIconData(property).iconColor,
                  }}
                >
                  {displayNumber ?? "-"}
                </div>
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-medium leading-snug wrap-break-word ${
                  isDisabled ? "text-data-light" : "text-data"
                }`}>
                  {property?.address1}
                </p>
                <p className="text-xs text-data-light">
                  {isCurrentLocation ? "Current location" : isHidden ? "Hidden in properties" : "Building stop"}
                </p>
              </div>
            </div>
            <div className="shrink-0 pt-0.5">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-xs font-medium text-data">Show in Tour</span>
                <Switch
                  checked={isIncludedInTourSchedule}
                  onChange={onToggleIncludeInTourSchedule}
                  size="small"
                  disabled={isHidden || isCurrentLocation}
                />
              </div>
            </div>
          </div>
          <EmeraldInput
            helperText="Enter up to 200 characters"
            label="Stop details"
            type="text"
            placeholder=""
            value={stopDetailsValue}
            onChange={onStopDetailsChange}
            onFocus={onStopDetailsFocus}
            onBlur={onStopDetailsBlur}
            maxLength={200}
          />
        </div>
      </div>
    </li>
  );
};



const TourScheduleDrawer = ({ onClose }: TourScheduleDrawerProps) => {
  const dispatch = useAppDispatch();
  const [updatePropertyMutation] = useUpdatePropertyMutation();
  const [updateSurveyMutation] = useUpdateSurveyMutation();
  const survey = useSurvey();
  const surveyId = survey?.id || "";
  const { quickDelete, withLoading } = useSimpleConfirmation();
  const customStopSensors = useSensors(useSensor(PointerSensor));

  // API hooks for tour stops
  const { data: tourStops = [], refetch: refetchTourStops } = useGetTourStopsQuery(surveyId, {
    skip: !surveyId,
    refetchOnMountOrArgChange: true,
  });
  const [createTourStop] = useCreateTourStopMutation();
  const [updateTourStop] = useUpdateTourStopMutation();
  const [deleteTourStop] = useDeleteTourStopMutation();
  const [reorderTourStops] = useReorderTourStopsMutation();
  const [attachTourStopToProperty] = useAttachTourStopToPropertyMutation();
  const [detachTourStopFromProperty] = useDetachTourStopFromPropertyMutation();

  // Local state for property stop details - for live preview
  const [stopDetailsState, setStopDetailsState] = useState<{
    [key: string]: string;
  }>({});
  // Store original values from backend to compare on blur
  const [originalStopDetails, setOriginalStopDetails] = useState<{
    [key: string]: string;
  }>({});

  // Local state for custom stop details - for live preview  
  const [customStopDetailsState, setCustomStopDetailsState] = useState<{
    [key: string]: string;
  }>({});
  const [originalCustomStopDetails, setOriginalCustomStopDetails] = useState<{
    [key: string]: string;
  }>({});

  // Custom stop modal state
  const [isCustomStopModalOpen, setIsCustomStopModalOpen] = useState(false);
  const [editingStopId, setEditingStopId] = useState<string | null>(null);
  const [customStopType, setCustomStopType] = useState<"CUSTOM_LOCATION" | "CURRENT_LOCATION">("CUSTOM_LOCATION");
  const [customStopLabel, setCustomStopLabel] = useState("");
  const [customStopAddress, setCustomStopAddress] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [isTourScheduleReordering, setIsTourScheduleReordering] = useState(false);

  // Track which stop-details field is currently focused so background survey /
  // tourStops updates cannot clobber an in-progress edit. Key format:
  //   "property:{propertyId}" or "stop:{stopId}"
  const focusedFieldRef = useRef<string | null>(null);
  // Track that a reorder mutation is in flight so the sortableItemOrder sync
  // effect doesn't snap the list back to server order before the response
  // lands.
  const pendingReorderRef = useRef(false);

  // Use survey.properties in Redux array order directly.
  // Manage Properties reorders mutate array position, and the getSurvey
  // refetch preserves server-canonical order — so array order IS the source
  // of truth. Sorting by .order caused re-shuffles when individual property
  // PATCHes returned .order values inconsistent with the local optimistic
  // values (server may renumber after a Manage Properties reorder while the
  // Redux slice still has stale .order on unrelated properties).
  const allProperties = useMemo(
    () => survey?.properties || [],
    [survey?.properties],
  );

  const tourNumberingMode = resolveTourNumberingMode(
    surveyId,
    survey?.tourNumberingMode,
  );
  const propertyDisplayNumbers = getTourPropertyDisplayNumbers({
    properties: allProperties,
    mode: tourNumberingMode,
  });

  // Build sortable list matching TourScheduleSectionPreview logic:
  // 1) Independent stops at top (order <= last property order)
  // 2) Each property (in order) + its attached stops
  // 3) Independent stops at bottom (order > last property order)
  const lastPropertyOrder = useMemo(
    () =>
      allProperties.length > 0
        ? Math.max(...allProperties.map((property) => property.order ?? 0))
        : -1,
    [allProperties],
  );

  // Independent stops: those with no propertyId (matching preview logic)
  const independentStops = useMemo(
    () =>
      tourStops
        .filter((stop) => !stop.propertyId)
        .sort((a, b) => a.order - b.order),
    [tourStops],
  );

  const independentStopsTop = useMemo(
    () =>
      independentStops.filter(
        (stop) => lastPropertyOrder === -1 || stop.order <= lastPropertyOrder,
      ),
    [independentStops, lastPropertyOrder],
  );
  const independentStopsBottom = useMemo(
    () =>
      independentStops.filter(
        (stop) => lastPropertyOrder !== -1 && stop.order > lastPropertyOrder,
      ),
    [independentStops, lastPropertyOrder],
  );

  const baseSortableItems: SortableItem[] = useMemo(() => {
    const items: SortableItem[] = [];

    // Add independent stops at top
    independentStopsTop.forEach((stop) => {
      items.push({
        id: `stop-${stop.stopId}`,
        type: "customStop",
        order: stop.order,
        data: stop,
      });
    });

    // Add each property + its attached stops (iterate through ALL properties for consistent order)
    allProperties.forEach((property) => {
      // In drawer, keep visible properties rendered even when "Show in Tour" is off
      // so users can re-enable and use renumbering choices.
      const shouldShowProperty =
        !property.isHidden &&
        !property.isCurrentLocation;

      if (shouldShowProperty) {
        items.push({
          id: `property-${property.propertyId}`,
          type: "property",
          order: property.order ?? 0,
          data: property,
        });
      }

      // Always add attached stops below the property (even if property is hidden/current location)
      // This matches preview behavior: attached stops render even if property card is toggled off
      const attachedStops = tourStops
        .filter((stop) => stop.propertyId === property.propertyId)
        .sort((a, b) => a.order - b.order);

      attachedStops.forEach((stop) => {
        items.push({
          id: `stop-${stop.stopId}`,
          type: "customStop",
          order: stop.order,
          data: stop,
        });
      });
    });

    // Add independent stops at bottom
    independentStopsBottom.forEach((stop) => {
      items.push({
        id: `stop-${stop.stopId}`,
        type: "customStop",
        order: stop.order,
        data: stop,
      });
    });

    return items;
  }, [allProperties, tourStops, independentStopsTop, independentStopsBottom]);

  const [sortableItemOrder, setSortableItemOrder] = useState<string[]>([]);

  useEffect(() => {
    const baseIds = baseSortableItems.map((item) => item.id);
    setSortableItemOrder((prev) => {
      // First render / uninitialized: adopt server order.
      if (prev.length === 0) return baseIds;

      // While a custom-stop reorder is in-flight, preserve local order —
      // the drag was optimistically applied and the server response hasn't
      // landed yet. Reconcile add/remove only.
      if (pendingReorderRef.current) {
        const baseIdSet = new Set(baseIds);
        const prevIdSet = new Set(prev);
        const kept = prev.filter((id) => baseIdSet.has(id));
        const added = baseIds.filter((id) => !prevIdSet.has(id));
        return [...kept, ...added];
      }

      // No pending reorder: adopt Redux truth (baseIds).
      // Property positions follow survey.properties array order; attached
      // stops follow their properties. Fix Q removed the .order-based sort
      // so this no longer causes visible drift on unrelated PATCHes.
      return baseIds;
    });
  }, [baseSortableItems]);

  const sortableItems = useMemo(() => {
    if (sortableItemOrder.length === 0) {
      return baseSortableItems;
    }

    const byId = new Map(baseSortableItems.map((item) => [item.id, item]));
    return sortableItemOrder
      .map((id) => byId.get(id))
      .filter((item): item is SortableItem => item !== undefined);
  }, [baseSortableItems, sortableItemOrder]);

  // Get current location properties for auto-populate / dropdown
  const currentLocationProperties = (survey?.properties || []).filter(
    (item: Property) => item?.isCurrentLocation === true
  );

  // Initialize / reconcile state when survey properties change.
  // Skips the currently-focused field so a background refetch cannot clobber
  // an in-progress edit.
  useEffect(() => {
    if (!survey?.properties) return;
    const focused = focusedFieldRef.current;

    setStopDetailsState((prev) => {
      const next = { ...prev };
      survey.properties.forEach((property) => {
        if (focused === `property:${property.propertyId}`) return;
        next[property.propertyId] = property.stopDetails || "";
      });
      return next;
    });

    setOriginalStopDetails((prev) => {
      const next = { ...prev };
      survey.properties.forEach((property) => {
        if (focused === `property:${property.propertyId}`) return;
        next[property.propertyId] = property.stopDetails || "";
      });
      return next;
    });
  }, [survey?.properties]);

  // Initialize / reconcile custom stop details from API data.
  // Skips the currently-focused field.
  useEffect(() => {
    const focused = focusedFieldRef.current;

    setCustomStopDetailsState((prev) => {
      const next = { ...prev };
      tourStops.forEach((stop) => {
        if (focused === `stop:${stop.stopId}`) return;
        next[stop.stopId] = stop.stopDetails || "";
      });
      return next;
    });

    setOriginalCustomStopDetails((prev) => {
      const next = { ...prev };
      tourStops.forEach((stop) => {
        if (focused === `stop:${stop.stopId}`) return;
        next[stop.stopId] = stop.stopDetails || "";
      });
      return next;
    });
  }, [tourStops]);

  // Helper function to update local state only. Redux (and downstream
  // consumers) are updated once per successful blur PATCH — not per keystroke
  // — to avoid churning survey.properties and cascading rebuilds of the
  // sortable items list.
  const updateStopDetailsState = (propertyId: string, value: string) => {
    setStopDetailsState((prev) => ({ ...prev, [propertyId]: value }));
  };

  // Handle input changes for live preview (onChange)
  const handleStopDetailsChange =
    (propertyId: string) => (e: BaseSyntheticEvent) => {
      updateStopDetailsState(propertyId, e.target.value);
    };

  const handleStopDetailsFocus = (propertyId: string) => () => {
    focusedFieldRef.current = `property:${propertyId}`;
  };

  // Handle blur event for API call (onBlur)
  const handleStopDetailsBlur = (propertyId: string) => () => {
    focusedFieldRef.current = null;
    const currentValue = stopDetailsState[propertyId] || "";
    const finalStopDetails = currentValue.trim();
    const originalValue = originalStopDetails[propertyId] || "";

    // Skip API call if value hasn't changed. Firing an unnecessary PATCH
    // triggers survey tag invalidation → refetch → sortableItemOrder reset,
    // which is one source of the visible reorder-on-blur behavior.
    if (finalStopDetails === originalValue) {
      return;
    }

    // Update local state with trimmed value
    updateStopDetailsState(propertyId, finalStopDetails);

    updatePropertyMutation({
      propertyId,
      updates: { stopDetails: finalStopDetails },
    })
      .unwrap()
      .then(() => {
        // Sync Redux once, after the server has confirmed the value. Doing
        // this only on success (not per keystroke) prevents survey.properties
        // churn from cascading into a sortable-list rebuild.
        dispatch(
          updateProperty({
            propertyId,
            updates: { stopDetails: finalStopDetails },
          }),
        );
        setOriginalStopDetails((prev) => ({
          ...prev,
          [propertyId]: finalStopDetails,
        }));
      })
      .catch((_error) => {
        console.error("Failed to update stop details for property");
        // Revert local state to previous value on error
        const revertValue = originalStopDetails[propertyId] || "";
        updateStopDetailsState(propertyId, revertValue);
      });
  };

  const handleOpenCustomStopModal = () => {
    setEditingStopId(null);
    setCustomStopType("CUSTOM_LOCATION");
    setCustomStopLabel("");
    setCustomStopAddress("");
    setSelectedPropertyId("");
    setIsCustomStopModalOpen(true);
  };

  const handleEditCustomStop = (stop: TourStop) => {
    setEditingStopId(stop.stopId);
    setCustomStopType(stop.stopType);
    setCustomStopLabel(stop.label);
    setCustomStopAddress(stop.address || "");
    // Preserve existing property attachment so save path does not accidentally
    // detach the stop from its property.
    setSelectedPropertyId(stop.propertyId ?? "");
    setIsCustomStopModalOpen(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (isTourScheduleReordering) {
      return;
    }

    if (!over || !surveyId || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    // Find source and destination indices in the unified sortable list
    const oldIndex = sortableItems.findIndex((item) => item.id === activeId);
    const newIndex = sortableItems.findIndex((item) => item.id === overId);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const movedItem = sortableItems[oldIndex];

    // Reorder the items array
    const reorderedItems = arrayMove(sortableItems, oldIndex, newIndex);

    // Update local visual order immediately.
    setSortableItemOrder(reorderedItems.map((item) => item.id));

    // Only the moved stop can change its property association. Every other
    // stop's propertyId is left untouched — this is the fix for stops
    // silently being reparented when an unrelated stop is dragged.
    let nextPropertyIdForMovedStop: string | undefined;
    let associationChanged = false;

    if (movedItem.type === "customStop") {
      const movedStop = movedItem.data as TourStop;
      const movedNewIndex = reorderedItems.findIndex(
        (item) => item.id === activeId,
      );

      // Determine the moved stop's new property: the nearest property card
      // strictly above its new position. If none, it becomes independent.
      let candidatePropertyId: string | undefined;
      for (let i = movedNewIndex - 1; i >= 0; i--) {
        const item = reorderedItems[i];
        if (item.type === "property") {
          candidatePropertyId = (item.data as Property).propertyId;
          break;
        }
      }

      nextPropertyIdForMovedStop = candidatePropertyId;
      associationChanged =
        (movedStop.propertyId ?? undefined) !==
        (nextPropertyIdForMovedStop ?? undefined);
    }

    // Build globally unique stop order values from the final visual stop
    // sequence (all custom stops, in visual order).
    const stopItems = reorderedItems.filter(
      (item): item is SortableItem => item.type === "customStop",
    );

    const stopOrders = stopItems.map((item, order) => {
      const stop = item.data as TourStop;
      return {
        stopId: stop.stopId,
        surveyId,
        order,
      };
    });

    if (stopOrders.length === 0 && !associationChanged) {
      return;
    }

    setIsTourScheduleReordering(true);
    pendingReorderRef.current = true;

    void (async () => {
      try {
        // 1) Association change for the moved stop only (if any).
        if (associationChanged && movedItem.type === "customStop") {
          const movedStop = movedItem.data as TourStop;
          if (movedStop.propertyId && nextPropertyIdForMovedStop) {
            await detachTourStopFromProperty({
              surveyId,
              stopId: movedStop.stopId,
            }).unwrap();
            await attachTourStopToProperty({
              surveyId,
              stopId: movedStop.stopId,
              body: { propertyId: nextPropertyIdForMovedStop },
            }).unwrap();
          } else if (!movedStop.propertyId && nextPropertyIdForMovedStop) {
            await attachTourStopToProperty({
              surveyId,
              stopId: movedStop.stopId,
              body: { propertyId: nextPropertyIdForMovedStop },
            }).unwrap();
          } else if (movedStop.propertyId && !nextPropertyIdForMovedStop) {
            await detachTourStopFromProperty({
              surveyId,
              stopId: movedStop.stopId,
            }).unwrap();
          }
        }

        // 2) Persist global stop order.
        if (stopOrders.length > 0) {
          await reorderTourStops({ surveyId, body: { stopOrders } }).unwrap();
        }

        await refetchTourStops();
      } catch (_error) {
        console.error("Failed to persist tour stop associations/order");
        await refetchTourStops();
      } finally {
        pendingReorderRef.current = false;
        setIsTourScheduleReordering(false);
      }
    })();
  };

  const handleCloseCustomStopModal = () => {
    setIsCustomStopModalOpen(false);
    setEditingStopId(null);
    setCustomStopType("CUSTOM_LOCATION");
    setCustomStopLabel("");
    setCustomStopAddress("");
    setSelectedPropertyId("");
  };

  const handleCancelCustomStop = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    handleCloseCustomStopModal();
  };

  const handleSaveCustomStop = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    if (!surveyId) return;

    // Validate required fields
    if (!customStopType || !customStopLabel.trim()) {
      console.error("Stop type and label are required");
      return;
    }

    // Validate property selection when there are multiple current locations
    if (
      customStopType === "CURRENT_LOCATION" &&
      !editingStopId &&
      currentLocationProperties.length > 1 &&
      !selectedPropertyId
    ) {
      console.error("Please select a location");
      return;
    }

    if (editingStopId) {
      // Update existing stop.
      // Optimistic cache patch on updateTourStop keeps the cache in sync;
      // manual refetch would just trigger a sortableItemOrder rebuild.
      updateTourStop({
        surveyId,
        stopId: editingStopId,
        body: {
          stopType: customStopType,
          label: customStopLabel.trim(),
          address: customStopAddress.trim(),
        },
      })
        .unwrap()
        .then(() => {
          handleCloseCustomStopModal();
        })
        .catch((_error) => {
          console.error("Failed to update tour stop");
        });
    } else {
      // Create new stop then place it at position 0.
      // Include ALL stops (attached + independent) in the reorder so
      // `order` values remain globally unique.
      createTourStop({
        surveyId,
        body: {
          stopType: customStopType,
          label: customStopLabel.trim(),
          address: customStopAddress.trim(),
          stopDetails: "",
        },
      })
        .unwrap()
        .then((newStop) => {
          handleCloseCustomStopModal();
          const existingSorted = [...tourStops].sort(
            (a, b) => a.order - b.order,
          );

          const stopOrders = [newStop, ...existingSorted].map(
            (stop, index) => ({
              stopId: stop.stopId,
              surveyId,
              order: index,
            }),
          );

          return reorderTourStops({ surveyId, body: { stopOrders } }).unwrap();
        })
        .catch((_error) => {
          console.error("Failed to create tour stop");
          refetchTourStops();
        });
    }
  };

  const handleDeleteCustomStop = (stopId: string) => {
    if (!surveyId) return;

    quickDelete({
      title: "Custom Stop",
      text: `Are you sure you want to delete this custom stop?`,
      id: "SC-ConfirmDeleteCustomStop",
    })
      .then((confirmed) => {
        if (confirmed) {
          withLoading(
            () => deleteTourStop({ surveyId, stopId }).unwrap(),
            {
              loadingText: "Deleting custom stop...",
              successText: "Custom stop deleted successfully!",
              errorText: "Failed to delete custom stop.",
            },
          )
            .then(() => refetchTourStops())
            .catch((_error) => {
              console.error("Failed to delete tour stop");
            });
        }
      })
      .catch((_error) => {
        console.error("Error in confirmation dialog");
      });
  };

  const handleCustomStopDetailsChange = (stopId: string) => (e: BaseSyntheticEvent) => {
    const value = e.target.value;
    setCustomStopDetailsState(prev => ({ ...prev, [stopId]: value }));
  };

  const handleCustomStopDetailsFocus = (stopId: string) => () => {
    focusedFieldRef.current = `stop:${stopId}`;
  };

  const handleCustomStopDetailsBlur = (stopId: string) => () => {
    focusedFieldRef.current = null;
    if (!surveyId) return;

    const currentValue = customStopDetailsState[stopId] || "";
    const finalStopDetails = currentValue.trim();

    // Only make API call if value changed
    if (finalStopDetails !== (originalCustomStopDetails[stopId] || "")) {
      updateTourStop({
        surveyId,
        stopId,
        body: { stopDetails: finalStopDetails },
      })
        .unwrap()
        .then(() => {
          // Optimistic cache patch keeps getTourStops in sync;
          // no manual refetch needed (refetch would rebuild the list and
          // could snap sortableItemOrder back).
          setOriginalCustomStopDetails(prev => ({
            ...prev,
            [stopId]: finalStopDetails,
          }));
        })
        .catch((_error) => {
          console.error("Failed to update tour stop details");
          // Revert to original value on error
          const originalValue = originalCustomStopDetails[stopId] || "";
          setCustomStopDetailsState(prev => ({ ...prev, [stopId]: originalValue }));
        });
    }
  };

  const handleStopTypeChange = (type: "CUSTOM_LOCATION" | "CURRENT_LOCATION") => (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCustomStopType(type);

    // Auto-populate from current location properties when switching to CURRENT_LOCATION
    if (type === "CURRENT_LOCATION" && !editingStopId) {
      if (currentLocationProperties.length === 1) {
        // Single current location — auto-populate immediately
        const property = currentLocationProperties[0];
        setSelectedPropertyId(property.propertyId);
        setCustomStopLabel(property.name || property.address1 || "");
        const addressParts = [
          property.address1,
          property.city,
          property.stateProvince,
          property.postalCode,
        ].filter(part => part && part.trim()).join(", ");
        setCustomStopAddress(addressParts);
      } else {
        // Multiple or none — clear fields and wait for selection
        setSelectedPropertyId("");
        setCustomStopLabel("");
        setCustomStopAddress("");
      }
    } else if (type === "CUSTOM_LOCATION" && !editingStopId) {
      // Clear auto-populated values when switching back
      setSelectedPropertyId("");
      setCustomStopLabel("");
      setCustomStopAddress("");
    }
  };

  const handlePropertySelection = (
    _: unknown,
    selectedValue: string | string[],
  ) => {
    if (typeof selectedValue !== "string") return;

    const propertyId = selectedValue;
    setSelectedPropertyId(propertyId);

    const selectedProperty = currentLocationProperties.find(
      (p) => String(p.propertyId) === String(propertyId),
    );
    if (!selectedProperty) return;

    setCustomStopLabel(selectedProperty.name || selectedProperty.address1 || "");
    const addressParts = [
      selectedProperty.address1,
      selectedProperty.city,
      selectedProperty.stateProvince,
      selectedProperty.postalCode,
    ].filter(part => part && part.trim()).join(", ");
    setCustomStopAddress(addressParts);
  };

  const promptHideNumberingChoice = async (): Promise<TourNumberingMode | null> => {
    const result = await Swal.fire({
      title: "Hide stop from Tour Schedule?",
      text: "Remaining stops can be re-numbered, or you can keep current numbering.",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Hide and Re-number",
      denyButtonText: "Hide and Keep numbering",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#003f2d",
      denyButtonColor: "#003f2d",
      reverseButtons: true,
      focusCancel: true,
      customClass: {
        popup: "tour-numbering-popup",
        actions: "emerald-button mt-0 tour-numbering-actions",
        cancelButton: "tour-numbering-cancel",
        denyButton: "tour-numbering-deny",
        confirmButton: "tour-numbering-confirm",
      },
    });

    if (result.isConfirmed) return "RENUMBER";
    if (result.isDenied) return "KEEP";
    return null;
  };

  const handleToggleTourPropertyVisibility =
    (propertyId: string) => async (checked: boolean) => {
      try {
        if (!checked) {
          const selectedMode = await promptHideNumberingChoice();
          if (!selectedMode) {
            return;
          }

          // 1) Hide stop first to avoid toggle bounce caused by survey refetch timing.
          await updatePropertyMutation({
            propertyId,
            updates: {
              isIncludeTourSchedule: false,
            },
          }).unwrap();

          // 2) Persist selected numbering mode (local immediate fallback + backend source of truth).
          if (surveyId) {
            setPersistedTourNumberingMode(surveyId, selectedMode);
            await updateSurveyMutation({
              id: surveyId,
              tourNumberingMode: selectedMode,
            })
              .unwrap()
              .catch((_error) => {
                console.error("Failed to update tour numbering mode");
              });
          }

          return;
        }

        await updatePropertyMutation({
          propertyId,
          updates: {
            isIncludeTourSchedule: checked,
          },
        }).unwrap();
      } catch (_error) {
        console.error("Failed to update tour schedule property visibility");
      }
    };

  return (
    <>
      <div className="relative flex flex-col text-data">
        {isTourScheduleReordering && (
          <div className="absolute inset-0 z-20 bg-white/70 flex items-center justify-center">
            <EmeraldProgressSpinner
              title={""}
              label={"Updating tour schedule..."}
              size="small"
              isWhite={false}
              determinate={false}
            />
          </div>
        )}
        <div className="drawer-header flex justify-between items-center color-data">
          <div className="flex justify-between items-center">
            <h2 className="text-xl/6 font-medium">Tour Schedule </h2>
            <EmeraldTooltip
              triggerElement={
                <EmeraldIcon
                  icon="info_outline"
                  type="filled"
                  className="text-xs mt-2 ml-3 cursor-default text-warning-icon"
                />
              }
              delayShow={0}
              delayHide={1000}
              followCursor={false}
              visible={false}
              placement={"bottom"}
            >
              <p className="text-xs">
                Your Tour Schedule order follows the order from the properties in
                your survey. If you {`don't`} see a property, that means it is
                toggled off in Manage Properties.
              </p>
            </EmeraldTooltip>
          </div>

          <EmeraldIcon icon="close" type="filled" onClick={onClose} />
        </div>
        <div className="drawer-section-item border-t border-divider-1 py-4">
          <div className="mb-4">
            <EmeraldButton
              label="Add custom stop"
              icon="add"
              onClick={handleOpenCustomStopModal}
              className="w-full bg-primary text-white border-primary hover:bg-primary-dark"
            />
          </div>
          <DndContext
            sensors={customStopSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableItems.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="flex flex-col gap-y-3">
                {sortableItems.map((item) => {
                  if (item.type === 'property') {
                    const property = item.data as Property;
                    
                    // Only render property card if visible (skip hidden/current location properties)
                    const shouldShowProperty =
                      !property.isHidden &&
                      !property.isCurrentLocation;

                    if (!shouldShowProperty) {
                      return null;
                    }

                    return (
                      <PropertyItem
                        key={item.id}
                        property={property}
                        displayNumber={propertyDisplayNumbers[property.propertyId]}
                        isIncludedInTourSchedule={property.isIncludeTourSchedule !== false}
                        isHidden={property.isHidden === true}
                        isCurrentLocation={property.isCurrentLocation === true}
                        onToggleIncludeInTourSchedule={handleToggleTourPropertyVisibility(property.propertyId)}
                        stopDetailsValue={stopDetailsState[property.propertyId] || ""}
                        onStopDetailsChange={handleStopDetailsChange(property.propertyId)}
                        onStopDetailsFocus={handleStopDetailsFocus(property.propertyId)}
                        onStopDetailsBlur={handleStopDetailsBlur(property.propertyId)}
                      />
                    );
                  } else {
                    const stop = item.data as TourStop;
                    return (
                      <CustomStopItem
                        key={item.id}
                        stop={stop}
                        stopDetailsValue={customStopDetailsState[stop.stopId] || ""}
                        onStopDetailsChange={handleCustomStopDetailsChange(stop.stopId)}
                        onStopDetailsFocus={handleCustomStopDetailsFocus(stop.stopId)}
                        onStopDetailsBlur={handleCustomStopDetailsBlur(stop.stopId)}
                        onEdit={() => handleEditCustomStop(stop)}
                        onDelete={() => handleDeleteCustomStop(stop.stopId)}
                      />
                    );
                  }
                })}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* Custom Stop Modal */}
      <EmeraldModal
        opened={isCustomStopModalOpen}
        onClose={handleCloseCustomStopModal}
        title={editingStopId ? "Edit custom stop" : "Add custom stop"}
        headerText={editingStopId ? "Edit custom stop" : "Add custom stop"}
        hasCloseIcon={true}
        className="tour-schedule-stop-modal"
        zIndex={1000}
        actionTemplate={
          <>
            <EmeraldButton
              label="Cancel"
              type="Secondary"
              onClick={handleCancelCustomStop}
            />
            <EmeraldButton
              label={editingStopId ? "Save changes" : "Add stop"}
              type="Primary"
              onClick={handleSaveCustomStop}
              disabled={
                !customStopType ||
                !customStopLabel.trim() ||
                (customStopType === "CURRENT_LOCATION" &&
                  !editingStopId &&
                  currentLocationProperties.length > 1 &&
                  !selectedPropertyId)
              }
            />
          </>
        }
      >
        <div className="py-2 flex flex-col gap-y-3">
          <div>
            <p className="text-sm font-medium text-data mb-2">
              Stop type <span className="text-critical">*</span>
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className={`px-3 py-1.5 rounded-md border text-sm ${
                  customStopType === "CUSTOM_LOCATION"
                    ? "border-primary text-primary bg-primary-lightest"
                    : "border-divider-2 text-data"
                }`}
                onClick={handleStopTypeChange("CUSTOM_LOCATION")}
              >
                <span className="inline-flex items-center gap-1">
                  <EmeraldIcon 
                    icon="location_on" 
                    type="outlined" 
                    className={customStopType === "CUSTOM_LOCATION" ? "" : "text-primary"}
                  />
                  Custom location
                </span>
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 rounded-md border text-sm ${
                  customStopType === "CURRENT_LOCATION"
                    ? "border-warning-icon text-warning-icon bg-warning-alert"
                    : "border-divider-2 text-data"
                }`}
                onClick={handleStopTypeChange("CURRENT_LOCATION")}
              >
                <span className="inline-flex items-center gap-1">
                  <EmeraldIcon 
                    icon="star" 
                    type="filled" 
                    className={customStopType === "CURRENT_LOCATION" ? "" : "text-warning-icon"}
                  />
                  Current location
                </span>
              </button>
            </div>
          </div>

          {/* Dropdown when multiple current location properties exist */}
          {customStopType === "CURRENT_LOCATION" && currentLocationProperties.length > 1 && !editingStopId && (
            <EmeraldSelect
              label="Select Location"
              name="propertySelection"
              value={selectedPropertyId || ""}
              onChange={handlePropertySelection}
              options={currentLocationProperties
                .filter((property) => !!property.propertyId)
                .map((property) => ({
                  label: property.name || property.address1 || "Unnamed Location",
                  value: property.propertyId,
                }))}
              placeholder="Select a location..."
              required
            />
          )}

          {/* Info when no current location properties exist */}
          {customStopType === "CURRENT_LOCATION" && currentLocationProperties.length === 0 && !editingStopId && (
            <div className="text-xs text-gray-600 p-2 bg-gray-50 rounded-md">
              No current location properties available.
            </div>
          )}

          <EmeraldInput
            label="Label"
            type="text"
            placeholder="e.g. Lunch at Nobu, Meeting point"
            value={customStopLabel}
            onChange={(e: BaseSyntheticEvent) => setCustomStopLabel(e.target.value)}
            required
            maxLength={200}
          />

          <EmeraldInput
            label="Address (optional)"
            type="text"
            placeholder="e.g. 123 Main St, Dallas, TX"
            value={customStopAddress}
            onChange={(e: BaseSyntheticEvent) => setCustomStopAddress(e.target.value)}
            helperText="Enter the address for this stop."
            maxLength={200}
          />
        </div>
      </EmeraldModal>
    </>
  );
};

export default TourScheduleDrawer;
